import type { FieldValue, Timestamp } from "firebase/firestore";

export type SenderType = "user" | "admin" | "system" | "ai";

export type ChatParticipant = {
  type: SenderType;
  id: string;
};

export type ChatLastMessage = {
  preview: string;
  senderType: SenderType;
  createdAt: Timestamp | Date | FieldValue | null;
};

export type ChatMessageDoc = {
  chatId: string;
  senderId?: string | null;
  senderType: SenderType;
  text: string;
  createdAt: Timestamp | Date | FieldValue;
  deliveryStatus: "sent" | "delivered";
  isReadByAdmin?: boolean;
  isReadByUser?: boolean;
  metadata?: Record<string, unknown>;
};

export type ChatThreadDoc = {
  status: "active" | "archived" | "merged";
  userId?: string | null;
  anonSessionId?: string | null;
  linkedSessionIds?: string[];
  participants: ChatParticipant[];
  createdAt: Timestamp | Date | FieldValue;
  updatedAt: Timestamp | Date | FieldValue;
  lastMessageAt?: Timestamp | Date | FieldValue | null;
  lastMessage?: ChatLastMessage | null;
  unreadCounts: {
    user: number;
    admin: number;
  };
  metadata?: Record<string, unknown>;
  redirectToChatId?: string | null;
};

export type ChatMessage = ChatMessageDoc & { id: string; createdAt: Date };
export type ChatThread = ChatThreadDoc & {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  lastMessageAt?: Date | null;
  lastMessage?: ChatLastMessage | null;
};

const toDate = (value?: Timestamp | Date | FieldValue | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as Timestamp).toDate === "function") {
    return (value as Timestamp).toDate();
  }
  return null;
};

export const mapMessage = (id: string, data: ChatMessageDoc): ChatMessage => ({
  id,
  chatId: data.chatId,
  senderId: data.senderId ?? undefined,
  senderType: data.senderType,
  text: data.text,
  deliveryStatus: data.deliveryStatus,
  isReadByAdmin: data.isReadByAdmin,
  isReadByUser: data.isReadByUser,
  metadata: data.metadata,
  createdAt: toDate(data.createdAt) ?? new Date()
});

export const mapThread = (id: string, data: ChatThreadDoc): ChatThread => ({
  id,
  status: data.status,
  userId: data.userId ?? null,
  anonSessionId: data.anonSessionId ?? null,
  linkedSessionIds: data.linkedSessionIds ?? [],
  participants: data.participants ?? [],
  createdAt: toDate(data.createdAt) ?? new Date(),
  updatedAt: toDate(data.updatedAt) ?? new Date(),
  lastMessageAt: toDate(data.lastMessageAt ?? null),
  lastMessage: data.lastMessage ?? null,
  unreadCounts: data.unreadCounts ?? { user: 0, admin: 0 },
  metadata: data.metadata ?? {},
  redirectToChatId: data.redirectToChatId ?? null
});
